import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "../services/api";
import { useAuth } from "../AuthContext";
import PasswordInput from "../components/PasswordInput";
import TurnstileWidget from "../components/TurnstileWidget";
import TwoFactorLogin from "../components/TwoFactorLogin";
import { jwtDecode } from "jwt-decode";

// Utility for email validation (not for username)
const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

// Very basic password strength meter: 0-4
const passwordStrength = (pw) => {
  let score = 0;
  if (pw.length >= 8) score++;
  if (/[A-Z]/.test(pw)) score++;
  if (/[0-9]/.test(pw)) score++;
  if (/[^A-Za-z0-9]/.test(pw)) score++;
  return score;
};

export default function AuthPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, isLoggedIn } = useAuth();

  // UI state
  const [view, setView] = useState("login"); // login | signup | forgot | 2fa
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [showResend, setShowResend] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [pendingAuth, setPendingAuth] = useState(null); // Store pending auth data for 2FA
  const [unverifiedEmail, setUnverifiedEmail] = useState(null); // Store email for resend verification

  // Form fields
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [rememberMe, setRememberMe] = useState(true);
  const [turnstileToken, setTurnstileToken] = useState("");

  // Show/hide password state (handled inside PasswordInput)
  const [fieldErrors, setFieldErrors] = useState({});

  // Refs for accessibility
  const usernameRef = useRef();
  const emailRef = useRef();
  const passwordRef = useRef();
  const confirmRef = useRef();

  // Focus correct field on mount or view change
  useEffect(() => {
    setTimeout(() => {
      if (view === "login" && usernameRef.current) usernameRef.current.focus();
      if (view === "signup" && emailRef.current) emailRef.current.focus();
      if (view === "forgot" && emailRef.current) emailRef.current.focus();
    }, 50);
  }, [view]);

  // Show message after email verification
  useEffect(() => {
    if (searchParams.get("verified") === "true") {
      setSuccess("Your email was verified. You can now log in.");
      setView("login");
    }
  }, [searchParams]);

  // Redirect if already logged in
  useEffect(() => {
    if (isLoggedIn) navigate("/dashboard");
  }, [isLoggedIn, navigate]);

  // Clear form state when switching views
  useEffect(() => {
    setError("");
    setSuccess("");
    setFieldErrors({});
    setShowResend(false);
    setUnverifiedEmail(null); // Clear unverified email state
    setTurnstileToken(""); // Reset Turnstile token when switching views
  }, [view]);

  // -------- Validation helpers --------
  const validateSignup = () => {
    const errors = {};
    if (!isValidEmail(email)) errors.email = "Enter a valid email address.";
    if (!username || username.length < 4 || isValidEmail(username))
      errors.username = "Username must be at least 4 characters, not an email.";
    if (!password || password.length < 8)
      errors.password = "Password must be at least 8 characters.";
    if (password !== confirm)
      errors.confirm = "Passwords do not match.";
    return errors;
  };

  const validateLogin = () => {
    const errors = {};
    if (!username) errors.username = "Enter your username.";
    if (!password) errors.password = "Enter your password.";
    return errors;
  };

  const validateForgot = () => {
    const errors = {};
    if (!isValidEmail(email)) errors.email = "Enter a valid email address.";
    return errors;
  };

  // -------- Field error focusing --------
  const focusFirstInvalid = (errors) => {
    if (errors.email && emailRef.current) emailRef.current.focus();
    else if (errors.username && usernameRef.current) usernameRef.current.focus();
    else if (errors.password && passwordRef.current) passwordRef.current.focus();
    else if (errors.confirm && confirmRef.current) confirmRef.current.focus();
  };

  // -------- Handlers --------
  const handleLogin = async (e) => {
    e.preventDefault();
    console.log('LOGIN ATTEMPT STARTED - Username:', username);
    setError("");
    setSuccess("");
    const errors = validateLogin();
    setFieldErrors(errors);
    if (Object.keys(errors).length) {
      focusFirstInvalid(errors);
      return;
    }
    setLoading(true);
    try {
      const loginData = { username, password, rememberMe };
      // Include turnstile token if it exists (empty string is fine since backend bypasses)
      loginData.turnstileToken = turnstileToken || '';
      console.log('MAKING API CALL TO /auth/login with data:', loginData);
      const res = await api.post("/auth/login", loginData, { withCredentials: true });
      console.log('API RESPONSE RECEIVED:', res.data);
      
      // Check if 2FA is required
      if (res.data.requires2FA) {
        setPendingAuth({
          username,
          rememberMe,
          tempToken: res.data.tempToken
        });
        setView("2fa");
        setLoading(false);
        return;
      }

      const { accessToken } = res.data;

      // 1. Decode the token to get the user data, including roles.
      const decodedUserData = jwtDecode(accessToken);

      // 2. Pass the full user object to your login function, not just the token string.
      login({
        token: accessToken,
        roles: decodedUserData.roles,
        userId: decodedUserData.userId
        // Add any other fields from the token you need globally
      });

      setSuccess("Login successful! Redirecting...");
      navigate("/dashboard");
    } catch (err) {
      console.error('LOGIN ERROR:', err);
      console.error('ERROR RESPONSE:', err.response?.data);
      
      // Handle email verification error specially
      if (err.response?.data?.code === 'EMAIL_NOT_VERIFIED') {
        setError(err.response.data.message);
        setUnverifiedEmail(err.response.data.email);
        setShowResend(true);
      } else {
        setError(err.response?.data?.message || "Login failed.");
        setFieldErrors({ password: "Check your credentials." });
        focusFirstInvalid({ password: true });
        setUnverifiedEmail(null);
        setShowResend(false);
      }
    }
    setLoading(false);
  };

  // Handle 2FA verification
  const handle2FAVerified = async (verificationResult) => {
    try {
      // Use the verification result to complete login
      const { accessToken } = verificationResult;
      
      if (accessToken) {
        const decodedUserData = jwtDecode(accessToken);
        
        login({
          token: accessToken,
          roles: decodedUserData.roles,
          userId: decodedUserData.userId,
          rememberMe: pendingAuth?.rememberMe
        });

        setSuccess("2FA verified! Redirecting...");
        navigate("/dashboard");
      }
    } catch (err) {
      setError("Failed to complete 2FA verification");
      console.error("2FA verification error:", err);
    }
  };

  // Handle 2FA cancellation
  const handle2FACancel = () => {
    setView("login");
    setPendingAuth(null);
    setError("");
    setSuccess("");
  };

  // Signup
  const handleSignup = async (e) => {
    e.preventDefault();
    setError(""); setSuccess(""); setShowResend(false);
    const errors = validateSignup();
    setFieldErrors(errors);
    if (Object.keys(errors).length) { focusFirstInvalid(errors); return; }
    setLoading(true);
    try {
      const res = await api.post("/auth/signup", { email, username, password, turnstileToken });
      setSuccess(res.data?.message || "Signup successful! Check your email for a verification link.");
      setShowResend(true);
      setEmail(""); setUsername(""); setPassword(""); setConfirm("");
      setView("login");
    } catch (err) {
      setError(err.response?.data?.message || "Signup failed.");
      if (err.response?.data?.field) setFieldErrors({ [err.response.data.field]: err.response.data.message });
      focusFirstInvalid(err.response?.data?.field ? { [err.response.data.field]: true } : {});
    }
    setLoading(false);
  };

  // Resend Verification (for signup/forgot flows)
  const handleResend = async () => {
    setResendLoading(true);
    setError(""); setSuccess("");
    try {
      const res = await api.post("/auth/resend-verification", { email });
      setSuccess(res.data?.message || "Verification email sent.");
    } catch (err) {
      setError(err.response?.data?.message || "Could not resend email.");
    }
    setResendLoading(false);
  };

  // Resend Verification (for login email verification failure)
  const handleResendLoginVerification = async () => {
    if (!unverifiedEmail) return;
    setResendLoading(true);
    setError(""); setSuccess("");
    try {
      const res = await api.post("/auth/resend-verification", { email: unverifiedEmail });
      setSuccess(res.data?.message || "Verification email sent.");
      setShowResend(false); // Hide resend button after successful send
    } catch (err) {
      setError(err.response?.data?.message || "Could not resend email.");
    }
    setResendLoading(false);
  };

  // Forgot password
  const handleForgot = async (e) => {
    e.preventDefault();
    setError(""); setSuccess("");
    const errors = validateForgot();
    setFieldErrors(errors);
    if (Object.keys(errors).length) { focusFirstInvalid(errors); return; }
    setLoading(true);
    try {
      const res = await api.post("/auth/forgot-password", { email, turnstileToken });
      setSuccess(res.data?.message || "Reset email sent (if registered).");
      setShowResend(true);
    } catch (err) {
      setError(err.response?.data?.message || "Error sending reset email.");
    }
    setLoading(false);
  };

  // -------- UI Section renderers --------
  const renderLogin = () => (
    <>
      <div className="form-group">
        <label htmlFor="username">Username</label>
        <input
          id="username" ref={usernameRef}
          type="text" autoComplete="username"
          value={username} disabled={loading}
          onChange={e => setUsername(e.target.value)}
          placeholder="Username" aria-invalid={!!fieldErrors.username}
        />
        {fieldErrors.username && <div className="field-error">{fieldErrors.username}</div>}
      </div>
      <PasswordInput
        id="login-password"
        value={password}
        onChange={e => setPassword(e.target.value)}
        placeholder="Your password"
        disabled={loading}
        inputRef={passwordRef}
        error={fieldErrors.password}
        autoComplete="current-password"
      />
      <div className="form-group remember-me">
        <input
          type="checkbox" id="remember-me"
          checked={rememberMe} onChange={e => setRememberMe(e.target.checked)}
          disabled={loading}
        />
        <label htmlFor="remember-me">Remember Me</label>
      </div>
      <TurnstileWidget
        onSuccess={setTurnstileToken}
        onError={() => setTurnstileToken("")}
        onExpire={() => setTurnstileToken("")}
        disabled={loading}
      />
      <button 
        type="submit" 
        className="action-button" 
        disabled={loading}
      >
        {loading ? "Logging in..." : "Login"}
      </button>
      <div className="helper-links">
        <button type="button" onClick={() => setView("forgot")} className="link-btn" disabled={loading}>
          Forgot password or username?
        </button>
        <button type="button" onClick={() => setView("signup")} className="link-btn" disabled={loading}>
          Need an account? Sign Up
        </button>
      </div>
      {showResend && unverifiedEmail && (
        <div style={{ marginTop: 12, padding: 12, backgroundColor: '#f8f9fa', borderRadius: 4, border: '1px solid #dee2e6' }}>
          <p style={{ margin: '0 0 8px 0', fontSize: 14, color: '#6c757d' }}>
            Email verification required for: <strong>{unverifiedEmail}</strong>
          </p>
          <button 
            type="button" 
            className="link-btn" 
            onClick={handleResendLoginVerification} 
            disabled={resendLoading}
            style={{ fontSize: 14 }}
          >
            {resendLoading ? "Sending..." : "Resend Verification Email"}
          </button>
        </div>
      )}
    </>
  );

  const renderSignup = () => (
    <>
      <div className="form-group">
        <label htmlFor="signup-email">Email</label>
        <input
          id="signup-email" ref={emailRef}
          type="email" autoComplete="email"
          value={email} disabled={loading}
          onChange={e => setEmail(e.target.value)}
          placeholder="you@email.com" aria-invalid={!!fieldErrors.email}
        />
        {fieldErrors.email && <div className="field-error">{fieldErrors.email}</div>}
      </div>
      <div className="form-group">
        <label htmlFor="signup-username">Username</label>
        <input
          id="signup-username" ref={usernameRef}
          type="text" autoComplete="username"
          value={username} disabled={loading}
          onChange={e => setUsername(e.target.value)}
          placeholder="Choose a username" aria-invalid={!!fieldErrors.username}
        />
        {fieldErrors.username && <div className="field-error">{fieldErrors.username}</div>}
        <small>Must not be an email address.</small>
      </div>
      <PasswordInput
        id="signup-password"
        value={password}
        onChange={e => setPassword(e.target.value)}
        placeholder="Create password"
        disabled={loading}
        inputRef={passwordRef}
        error={fieldErrors.password}
        autoComplete="new-password"
      />
      <div style={{ margin: "6px 0 8px 0" }}>
        <PasswordStrengthMeter password={password} />
      </div>
      <PasswordInput
        id="signup-confirm"
        value={confirm}
        onChange={e => setConfirm(e.target.value)}
        placeholder="Confirm password"
        disabled={loading}
        inputRef={confirmRef}
        error={fieldErrors.confirm}
        autoComplete="new-password"
      />
      <TurnstileWidget
        onSuccess={setTurnstileToken}
        onError={() => setTurnstileToken("")}
        onExpire={() => setTurnstileToken("")}
        disabled={loading}
      />
      <button 
        type="submit" 
        className="action-button" 
        disabled={loading}
      >
        {loading ? "Signing up..." : "Sign Up"}
      </button>
      <div className="helper-links">
        <button type="button" onClick={() => setView("login")} className="link-btn" disabled={loading}>Already have an account? Login</button>
      </div>
      {showResend && (
        <div style={{ marginTop: 8 }}>
          <button type="button" className="link-btn" onClick={handleResend} disabled={resendLoading}>
            {resendLoading ? "Resending..." : "Resend verification email"}
          </button>
        </div>
      )}
    </>
  );

  const renderForgot = () => (
    <>
      <div className="form-group">
        <label htmlFor="forgot-email">Your Email</label>
        <input
          id="forgot-email" ref={emailRef}
          type="email" autoComplete="email"
          value={email} disabled={loading}
          onChange={e => setEmail(e.target.value)}
          placeholder="you@email.com" aria-invalid={!!fieldErrors.email}
        />
        {fieldErrors.email && <div className="field-error">{fieldErrors.email}</div>}
      </div>
      <TurnstileWidget
        onSuccess={setTurnstileToken}
        onError={() => setTurnstileToken("")}
        onExpire={() => setTurnstileToken("")}
        disabled={loading}
      />
      <button 
        type="submit" 
        className="action-button" 
        disabled={loading}
      >
        {loading ? "Sending..." : "Send Reset Email"}
      </button>
      <div className="helper-links">
        <button type="button" onClick={() => setView("login")} className="link-btn" disabled={loading}>Back to Login</button>
      </div>
      {showResend && (
        <div style={{ marginTop: 8 }}>
          <button type="button" className="link-btn" onClick={handleResend} disabled={resendLoading}>
            {resendLoading ? "Resending..." : "Resend password reset email"}
          </button>
        </div>
      )}
    </>
  );

  // Handle 2FA view separately since it's not a form
  if (view === "2fa") {
    return (
      <div className="auth-container">
        <div className="auth-card" style={{ minWidth: 340, maxWidth: 420 }}>
          {error && <div className="error-message" role="alert">{error}</div>}
          {success && <div className="success-message" role="status">{success}</div>}
          
          <TwoFactorLogin 
            onVerified={handle2FAVerified}
            onCancel={handle2FACancel}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="auth-container">
      <form
        className="auth-card"
        style={{ minWidth: 340, maxWidth: 370 }}
        autoComplete="off"
        onSubmit={
          view === "login"
            ? handleLogin
            : view === "signup"
            ? handleSignup
            : handleForgot
        }
        aria-live="polite"
      >
        <h2 style={{ margin: "0 0 18px 0", fontWeight: 700, textAlign: "center" }}>
          {view === "login" && "Login"}
          {view === "signup" && "Sign Up"}
          {view === "forgot" && "Forgot Something?"}
        </h2>

        {/* Status */}
        {error && <div className="error-message" role="alert">{error}</div>}
        {success && <div className="success-message" role="status">{success}</div>}

        {/* Form Content */}
        {view === "login" && renderLogin()}
        {view === "signup" && renderSignup()}
        {view === "forgot" && renderForgot()}
      </form>
    </div>
  );
}

// -------- Password Strength Meter (inline) -------
function PasswordStrengthMeter({ password }) {
  const score = passwordStrength(password);
  const labels = ["Too weak", "Weak", "Okay", "Good", "Strong"];
  const colors = ["#e53e3e", "#f6ad55", "#ecc94b", "#48bb78", "#3182ce"];
  return (
    <div style={{ height: 9, width: "100%", marginTop: 2 }}>
      <div
        style={{
          width: `${(score / 4) * 100}%`,
          height: 9,
          background: colors[score] || "#eee",
          borderRadius: 4,
          transition: "width .25s"
        }}
        aria-hidden="true"
      />
      <div style={{ fontSize: 12, color: "#777", marginTop: 1 }}>
        {password ? labels[score] : ""}
      </div>
    </div>
  );
}
